"use client";

import { useState, useEffect, useCallback } from "react";
import { TableContainer } from "@/components/common/TableContainer";
import type {
  PickupLocation as PickupLocationType,
  AddressFields,
} from "@/types/models/pickupLocation";
import { toast } from "sonner";
import { DataView } from "@/components/common/DataView";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Edit3, Trash2, X, Warehouse, RefreshCw } from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import { StandardFormInput, StandardFormSelect } from "@/components/common/StandardFormInput";
import { ActionButton } from "@/components/common/DataViewTypes";
import { transformStatesCitiesData } from "@/utils/statesCitiesTransform";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchPickupLocationsRequest,
  createPickupLocationRequest,
  updatePickupLocationRequest,
  deletePickupLocationRequest,
  setDefaultPickupLocationRequest,
  resetFormState,
} from "@/store/slices/pickupLocation.slice";

interface City {
  id: number;
  name: string;
  latitude: string;
  longitude: string;
}

interface State {
  id: number;
  name: string;
  state_code: string;
  latitude: string;
  longitude: string;
  type: string;
  cities: City[];
}

interface AddPickupForm {
  name: string;
  address_line_one: string;
  address_line_two: string;
  city: string;
  state: string;
  postal_code: string;
  is_active: boolean;
}

const initialFormData: AddPickupForm = {
  name: "",
  address_line_one: "",
  address_line_two: "",
  city: "",
  state: "",
  postal_code: "",
  is_active: true,
};

const Pickup = () => {
  const dispatch = useAppDispatch();
  const [pageIndex, setPageIndex] = useState(0);
  const {
    items: pickupAddresses,
    loaded,
    loading,
    error,
    pagination,
    formState,
  } = useAppSelector((state) => state.pickupLocations);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<PickupLocationType | null>(
    null,
  );
  const [addForm, setAddForm] = useState<AddPickupForm>(initialFormData);

  // State and City data
  const [states, setStates] = useState<State[]>([]);
  const [availableCities, setAvailableCities] = useState<City[]>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(true);

  const fetchPickupLocations = useCallback(() => {
    dispatch(
      fetchPickupLocationsRequest({
        page: pageIndex + 1,
        page_size: pagination.pageSize || 10,
        sort_by: "created_at",
        sort_order: "desc",
      }),
    );
  }, [dispatch, pageIndex, pagination.pageSize]);

  useEffect(() => {
    if (loading) return;

    const hasNotFoundError = error?.status === 404;
    if (hasNotFoundError) return;

    const currentPageIndex = Math.max(0, (pagination.page || 1) - 1);
    const hasPageMismatch = currentPageIndex !== pageIndex;

    if (!loaded && pickupAddresses.length === 0) {
      fetchPickupLocations();
      return;
    }

    if (loaded && hasPageMismatch) {
      fetchPickupLocations();
    }
  }, [loading, error?.status, loaded, pickupAddresses.length, pagination.page, pageIndex, fetchPickupLocations]);

  // Load states data
  useEffect(() => {
    const loadStates = async () => {
      try {
        const response = await fetch("/states-cities.json");
        const rawData: Record<string, string[]> = await response.json();
        const transformedData = transformStatesCitiesData(rawData);
        setStates(transformedData);
      } catch (error) {
        console.error("Failed to load states data:", error);
      } finally {
        setIsLoadingStates(false);
      }
    };

    loadStates();
  }, []);

  // Update cities when state changes
  useEffect(() => {
    if (addForm.state && states.length > 0) {
      const state = states.find((s) => s.name === addForm.state);
      if (state) {
        setAvailableCities(state.cities);
      } else {
        setAvailableCities([]);
      }
    } else {
      setAvailableCities([]);
    }
  }, [addForm.state, states]);

  const handleAddPickupAddress = () => {
    if (
      addForm.name &&
      addForm.address_line_one &&
      addForm.city &&
      addForm.state &&
      addForm.postal_code
    ) {
      const addressData: AddressFields = {
        address_line_one: addForm.address_line_one,
        address_line_two: addForm.address_line_two || undefined,
        city: addForm.city,
        state: addForm.state,
        postal_code: addForm.postal_code,
      };

      if (editingAddress) {
        dispatch(
          updatePickupLocationRequest({
            id: editingAddress.id,
            data: {
              name: addForm.name,
              address: addressData,
              is_active: addForm.is_active,
            },
          }),
        );
      } else {
        dispatch(
          createPickupLocationRequest({
            name: addForm.name,
            address: addressData,
            is_active: addForm.is_active,
            is_default: pickupAddresses.length === 0,
          }),
        );
      }
    }
  };

  const resetForm = () => {
    setAddForm(initialFormData);
    setEditingAddress(null);
  };

  const handleEditPickupAddress = (address: PickupLocationType) => {
    setEditingAddress(address);
    setAddForm({
      name: address.name,
      address_line_one: address.address.address_line_one,
      address_line_two: address.address.address_line_two || "",
      city: address.address.city,
      state: address.address.state,
      postal_code: address.address.postal_code,
      is_active: address.is_active,
    });
    setIsAddModalOpen(true);
  };

  const handleRemovePickupAddress = (id: string) => {
    dispatch(deletePickupLocationRequest({ id }));
  };

  const setAsDefault = (id: string) => {
    dispatch(setDefaultPickupLocationRequest({ id }));
  };

  useEffect(() => {
    if (formState.success) {
      if (formState.action === "create") {
        toast.success("Pickup location created successfully");
        resetForm();
        setIsAddModalOpen(false);
      } else if (formState.action === "update") {
        toast.success("Pickup location updated successfully");
        resetForm();
        setIsAddModalOpen(false);
      } else if (formState.action === "delete") {
        toast.success("Pickup location deleted successfully");
      } else if (formState.action === "set-default") {
        toast.success("Default pickup location updated");
      }

      dispatch(resetFormState());
    }
  }, [formState.success, formState.action, dispatch]);

  useEffect(() => {
    if (formState.error?.message) {
      toast.error(formState.error.message);
      dispatch(resetFormState());
    }
  }, [formState.error, dispatch]);

  useEffect(() => {
    if (error?.message) {
      toast.error(error.message);
    }
  }, [error]);

  const columns: ColumnDef<PickupLocationType>[] = [
    {
      accessorKey: "name",
      header: "Warehouse",
      size: 450,
      minSize: 400,
      cell: ({ row }) => {
        const address = row.original;
        return (
          <div className="flex items-center gap-3 min-w-75">
            <div className="h-10 w-10 bg-muted/50 rounded-lg flex items-center justify-center">
              <Warehouse className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-foreground">
                {address.name}
              </p>
              <div className="text-xs text-muted-foreground">
                {address.address.postal_code}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "address",
      header: "Address",
      cell: ({ row }) => {
        const location = row.original;
        return (
          <div className="text-sm">
            <div className="text-foreground">{location.address.address_line_one}</div>
            {location.address.address_line_two && (
              <div className="text-muted-foreground text-xs">
                {location.address.address_line_two}
              </div>
            )}
            <div className="text-muted-foreground text-xs">
              {location.address.city}, {location.address.state}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "is_active",
      header: "Status",
      cell: ({ row }) => {
        const location = row.original;
        return (
          <Badge
            className={
              location.is_active
                ? "bg-green-500/20 text-green-600 hover:bg-green-500/20"
                : "bg-gray-500/20 text-gray-600 hover:bg-gray-500/20"
            }
          >
            {location.is_active ? "Active" : "Inactive"}
          </Badge>
        );
      },
    },
    {
      accessorKey: "default",
      header: "Default",
      cell: ({ row }) => {
        const location = row.original;
        return location.is_default ? (
          <Badge className="bg-blue-500/20 text-blue-600 hover:bg-blue-500/20">
            Default
          </Badge>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAsDefault(location.id)}
            className="text-xs h-6 px-2 text-muted-foreground hover:text-foreground"
          >
            Set as Default
          </Button>
        );
      },
    },
    {
      accessorKey: "updated_at",
      header: "Last Updated",
      size: 120,
      cell: ({ row }) => {
        const date = new Date(row.original.updated_at);
        return (
          <div className="text-sm text-muted-foreground">
            {date.toLocaleDateString()}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      size: 120,
      cell: ({ row }) => {
        const address = row.original;
        return (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handleEditPickupAddress(address)}
            >
              <Edit3 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
              onClick={() => handleRemovePickupAddress(address.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  // Create action buttons
  const actionButtons: ActionButton[] = [
    {
      label: "Refresh",
      icon: <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />,
      onClick: fetchPickupLocations,
      variant: "outline",
      disabled: loading,
    },
    {
      label: "Add Pickup Address",
      icon: <Plus className="h-3 w-3" />,
      onClick: () => setIsAddModalOpen(true),
      variant: "primary",
    },
  ];

  return (
    <div>
      <TableContainer>
        <div className="bg-secondary-background rounded-lg p-4">
          <DataView
            title="Pickup Locations"
            description="Manage your warehouse and pickup addresses"
            columns={columns}
            data={pickupAddresses}
            pagination={pagination}
            pageIndex={pageIndex}
            onPageChange={setPageIndex}
            loading={loading}
            error={error}
            emptyStateType="general"
            actionButtons={actionButtons}
          />
        </div>
      </TableContainer>

      {/* Add/Edit Pickup Address Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-150 max-h-[80vh] overflow-y-auto bg-secondary-background">
          <DialogHeader>
            <DialogTitle>
              {editingAddress
                ? "Edit Pickup Address"
                : "Add New Pickup Address"}
            </DialogTitle>
            <DialogDescription>
              {editingAddress
                ? "Update the pickup address details."
                : "Add a new warehouse location for order pickup."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            {/* Warehouse Name */}
            <StandardFormInput
              label="Location Name"
              placeholder="e.g., Main Warehouse, Secondary Warehouse"
              value={addForm.name}
              onChange={(e) =>
                setAddForm((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
            />

            {/* Address Line 1 */}
            <StandardFormInput
              label="Address Line 1"
              placeholder="Enter address line 1"
              value={addForm.address_line_one}
              onChange={(e) =>
                setAddForm((prev) => ({
                  ...prev,
                  address_line_one: e.target.value,
                }))
              }
            />

            {/* Address Line 2 */}
            <StandardFormInput
              label="Address Line 2 (Optional)"
              placeholder="Enter address line 2"
              value={addForm.address_line_two}
              onChange={(e) =>
                setAddForm((prev) => ({
                  ...prev,
                  address_line_two: e.target.value,
                }))
              }
            />

            {/* State and City Selectors */}
            <div className="grid grid-cols-2 gap-4">
              <StandardFormSelect
                label="State"
                value={addForm.state}
                onValueChange={(value) => {
                  setAddForm((prev) => ({ ...prev, state: value, city: "" }));
                }}
                options={states.map((state) => ({
                  label: state.name,
                  value: state.name,
                }))}
                placeholder={isLoadingStates ? "Loading states..." : "Select State"}
                disabled={isLoadingStates}
              />
              <StandardFormSelect
                label="City"
                value={addForm.city}
                onValueChange={(value) => {
                  setAddForm((prev) => ({ ...prev, city: value }));
                }}
                options={availableCities.map((city) => ({
                  label: city.name,
                  value: city.name,
                }))}
                placeholder={
                  !addForm.state
                    ? "Select state first"
                    : availableCities.length === 0
                      ? "No cities available"
                      : "Select City"
                }
                disabled={!addForm.state || availableCities.length === 0}
              />
            </div>

            {/* Pincode */}
            <StandardFormInput
              label="Postal Code"
              placeholder="Enter postal code"
              value={addForm.postal_code}
              onChange={(e) =>
                setAddForm((prev) => ({ ...prev, postal_code: e.target.value }))
              }
            />

            {/* Active Status */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                checked={addForm.is_active}
                onChange={(e) =>
                  setAddForm((prev) => ({
                    ...prev,
                    is_active: e.target.checked,
                  }))
                }
                className="h-4 w-4 rounded border-gray-300"
              />
              <label
                htmlFor="is_active"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Active Location
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                resetForm();
                setIsAddModalOpen(false);
              }}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleAddPickupAddress}
              disabled={
                formState.loading ||
                !addForm.name ||
                !addForm.address_line_one ||
                !addForm.city ||
                !addForm.state ||
                !addForm.postal_code
              }
            >
              <Plus className="h-4 w-4 mr-2" />
              {editingAddress ? "Update Address" : "Add Address"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Pickup;
